import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { isExhibitionArtificialEmail, type ChangeEmailDto } from '@paladin-monorepo/common';

import API from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/forms/PasswordInput';
import { passwordMinLength } from '@/config/passwordMinLength';

/**
 * Persistent banner shown while the current user still uses the artificial
 * support+<ico>@monomo.ai email from the exhibition signup. Opens a dialog to set
 * a business email + new password (applied after email verification).
 */
export function ExhibitionClaimBanner() {
  const { t } = useTranslation();
  const { user, refetchUser } = useAuth({ tmp: 'ExhibitionClaimBanner' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!user || !isExhibitionArtificialEmail(user.email)) {
    return null;
  }

  const pendingEmail = user.pendingEmail ?? null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);
    if (!email.trim()) {
      setError(t('exhibition.claimBanner.errors.emailRequired'));
      return;
    }
    if (password.length < passwordMinLength) {
      setError(
        t('exhibition.claimBanner.errors.passwordTooShort', { minLength: passwordMinLength }),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await API.post('/me/change-email', {
        newEmail: email.trim(),
        newPassword: password,
      } satisfies ChangeEmailDto);
      await refetchUser();
      toast.success(t('exhibition.claimBanner.sentToast', { email: email.trim() }));
      setDialogOpen(false);
      setEmail('');
      setPassword('');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError(t('exhibition.claimBanner.errors.emailExists'));
      } else {
        setError(t('exhibition.claimBanner.errors.default'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Alert className="rounded-none border-x-0 border-t-0 bg-brand-primary/10">
        <Mail />
        <AlertTitle>
          {pendingEmail
            ? t('exhibition.claimBanner.pendingTitle')
            : t('exhibition.claimBanner.title')}
        </AlertTitle>
        <AlertDescription className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>
            {pendingEmail
              ? t('exhibition.claimBanner.pendingDescription', { email: pendingEmail })
              : t('exhibition.claimBanner.description')}
          </span>
          <Button size="sm" className="shrink-0 text-white" onClick={() => setDialogOpen(true)}>
            {pendingEmail
              ? t('exhibition.claimBanner.pendingCta')
              : t('exhibition.claimBanner.cta')}
          </Button>
        </AlertDescription>
      </Alert>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>{t('exhibition.claimBanner.dialog.title')}</DialogTitle>
              <DialogDescription>
                {t('exhibition.claimBanner.dialog.description')}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2">
              <Label htmlFor="claim-email">{t('exhibition.claimBanner.dialog.emailLabel')}</Label>
              <Input
                id="claim-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('exhibition.claimBanner.dialog.emailPlaceholder')}
                autoComplete="email"
                disabled={isSubmitting}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="claim-password">
                {t('exhibition.claimBanner.dialog.passwordLabel')}
              </Label>
              <PasswordInput
                id="claim-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('exhibition.claimBanner.dialog.passwordPlaceholder')}
                autoComplete="new-password"
                minLength={passwordMinLength}
                disabled={isSubmitting}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t('exhibition.claimBanner.dialog.submitting')
                  : t('exhibition.claimBanner.dialog.submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
